import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Pagination, PaginationItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { patientAppointmentHeadingList, patientAppointmentHeadingListTab3, patientAppointmentHeadingListTab4, patientAppointmentHeadingListUnfinished } from "../../../constant/staticLists";
import * as mainService from "../../../services/mainService.js";
import strings from "../../../constant/strings";
import Tables from "../../../components/controls/Tables";
import { LoadingContainer, NoDetailsContainer } from "../../../assets/styles/CommonStyles";
import ChatAndCall from "../../../components/Chat/Chat.js";

const AppointMentList = ({ searchValue, tabValue, setBookAppointment, setStep }) => {
  const { userData, patientAppointmentList, loading } = useSelector((state) => ({
    userData: state.auth.userData,
    patientAppointmentList: state.appointment.patientAppointmentList || [],
    loading: state.appointment.loading
  }));
  
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(7); // You can adjust this as needed
  const [showChat, setShowChat] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    const now = new Date();

    const isExpired = (item) => {
      // Use end_time if available, otherwise fall back to start_time
      const apptTime = item.end_time
        ? new Date(item.end_time)
        : item.start_time
        ? new Date(item.start_time)
        : null;
      return apptTime && apptTime < now;
    };

    const matchesSearch = (item) =>
      searchValue === "" ||
      item.doctorInfo?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.doctorInfo?.department?.toLowerCase().includes(searchValue.toLowerCase());

    let filtered;
    if (tabValue === 3) {
      // Unfinished: approved appointments whose time has already passed
      filtered = patientAppointmentList.filter((item) => isExpired(item) && matchesSearch(item));
    } else if (tabValue === 0) {
      // Upcoming: approved appointments whose time has NOT passed yet
      filtered = patientAppointmentList.filter((item) => !isExpired(item) && matchesSearch(item));
    } else {
      filtered = patientAppointmentList.filter((item) => matchesSearch(item));
    }

    setList(filtered);
  }, [patientAppointmentList, searchValue, tabValue]);

  useEffect(() => {
    // Reset to the first page when the list or search value changes
    setPage(1);
  }, [list]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleCancelAppointment = async (id) => {
    let value = { token: userData.token, data: { appointmentId: id } };
    const res = await mainService.cancelAppointmentByPatientApi(value);
    if (res.response_type === "fail") {
      // toast.error(res?.response?.message, { position: "bottom-center", closeButton: false })
    } else {
      // toast.success(res?.response?.message, { position: "bottom-center", closeButton: false })
      let updatedList = list.filter((item) => item._id !== id);
      setList(updatedList);
    }
  };

  const handleBookAgain = () => {
    setBookAppointment(1);
  };

  const handleChatOpen = (appointmentItem) => {
    setSelectedAppointment(appointmentItem);
    setShowChat(true);
  };

  const handleChatClose = () => {
    setShowChat(false);
    setSelectedAppointment(null);
  };

  // Calculate the start and end index for pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedList = list.slice(startIndex, endIndex);

  return (
    <>
      {loading ? (
        <LoadingContainer>{strings.loading}</LoadingContainer>
      ) : paginatedList && paginatedList.length > 0 ? (
        <>
          <Tables
            data={paginatedList}
            handleCancleAppointment={handleCancelAppointment}
            handleBookAgain={handleBookAgain}
            handleChatOpen={handleChatOpen}
            fieldsOrder={
              tabValue === 0 || tabValue === 1
                ? patientAppointmentHeadingList
                : tabValue === 2
                ? patientAppointmentHeadingListTab3
                : tabValue === 3
                ? patientAppointmentHeadingListUnfinished
                : patientAppointmentHeadingListTab4
            }
          />
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
            <Pagination
              count={Math.ceil(list.length / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              renderItem={(item) => (
                <PaginationItem
                  slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                  {...item}
                />
              )}
            />
          </div>
        </>
      ) : (
        <NoDetailsContainer>{strings.noAppointmentAvailable}</NoDetailsContainer>
      )}
      
      {showChat && selectedAppointment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'transparent',
            padding: '0',
            borderRadius: '8px',
            width: '80%',
            maxWidth: '800px',
            height: '80%',
            maxHeight: '600px',
            position: 'relative',
            border: 'none'
          }}>
            <ChatAndCall 
              currentUser={{
                socketId: userData._id,
                name: userData.name || 'Patient'
              }}
              receiver={{
                socketId: selectedAppointment.doctorInfo._id,
                name: selectedAppointment.doctorInfo.name
              }}
              onClose={handleChatClose}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(AppointMentList);
